import React, { useEffect, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../firebase';
import { useStudents } from '../lib/useStudents';
import { useDataStore } from '../lib/stores/dataStore';
import { collection, getDocs } from 'firebase/firestore';
import { Icons } from '../lib/Icons';

const StudentLoadingDiagnostic = () => {
  const [user, authLoading] = useAuthState(auth);
  const { students, loading: studentsLoading, error: studentsError } = useStudents();
  const storeStudents = useDataStore((s) => s.students);
  const storeLoading = useDataStore((s) => s.studentsLoading);
  const storeError = useDataStore((s) => s.studentsError);
  const [directFetchResult, setDirectFetchResult] = useState(null);
  const [directFetchError, setDirectFetchError] = useState(null);

  // Try to fetch students directly from Firestore
  useEffect(() => {
    const directFetch = async () => {
      try {
        console.log('🔍 Attempting direct Firestore fetch...');
        const querySnapshot = await getDocs(collection(db, 'students'));
        const directStudents = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('✅ Direct fetch successful:', directStudents.length, 'students');
        setDirectFetchResult(directStudents);
      } catch (error) {
        console.error('❌ Direct fetch failed:', error);
        setDirectFetchError(error.message);
      }
    };

    if (user && !authLoading) {
      directFetch();
    }
  }, [user, authLoading]);

  const diagnosticData = {
    auth: {
      isAuthenticated: !!user,
      authLoading,
      userEmail: user?.email,
      userId: user?.uid
    },
    useStudentsHook: {
      studentsCount: students?.length || 0,
      loading: studentsLoading,
      error: studentsError?.message
    },
    dataStore: {
      studentsCount: storeStudents?.length || 0,
      loading: storeLoading,
      error: storeError?.message
    },
    directFetch: {
      studentsCount: directFetchResult?.length || 0,
      error: directFetchError
    }
  };

  return (
    <div className="bg-white shadow-lg rounded-lg p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center"><Icons.Wrench className="w-6 h-6 mr-2" /> Student Loading Diagnostic</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Authentication Status */}
        <div className="border border-gray-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center"><Icons.Lock className="w-5 h-5 mr-2" /> Authentication</h3>
          <div className="space-y-2 text-sm">
            <div className={`flex items-center ${diagnosticData.auth.isAuthenticated ? 'text-green-600' : 'text-red-600'}`}>
              {diagnosticData.auth.isAuthenticated ? <Icons.CheckCircle className="w-4 h-4 mr-2" /> : <Icons.XCircle className="w-4 h-4 mr-2" />}
              Authenticated: {diagnosticData.auth.isAuthenticated ? 'Yes' : 'No'}
            </div>
            <div className={`flex items-center ${!diagnosticData.auth.authLoading ? 'text-green-600' : 'text-yellow-600'}`}>
              {!diagnosticData.auth.authLoading ? <Icons.CheckCircle className="w-4 h-4 mr-2" /> : <Icons.Hourglass className="w-4 h-4 mr-2" />}
              Loading: {diagnosticData.auth.authLoading ? 'Yes' : 'No'}
            </div>
            {diagnosticData.auth.userEmail && (
              <div className="text-gray-600">
                Email: {diagnosticData.auth.userEmail}
              </div>
            )}
            {diagnosticData.auth.userId && (
              <div className="text-gray-600 text-xs">
                UID: {diagnosticData.auth.userId}
              </div>
            )}
          </div>
        </div>

        {/* useStudents Hook */}
        <div className="border border-gray-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center"><Icons.Cog6Tooth className="w-5 h-5 mr-2" /> useStudents Hook</h3>
          <div className="space-y-2 text-sm">
            <div className={`flex items-center ${diagnosticData.useStudentsHook.studentsCount > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {diagnosticData.useStudentsHook.studentsCount > 0 ? <Icons.CheckCircle className="w-4 h-4 mr-2" /> : <Icons.XCircle className="w-4 h-4 mr-2" />}
              Students: {diagnosticData.useStudentsHook.studentsCount}
            </div>
            <div className={`flex items-center ${!diagnosticData.useStudentsHook.loading ? 'text-green-600' : 'text-yellow-600'}`}>
              {!diagnosticData.useStudentsHook.loading ? <Icons.CheckCircle className="w-4 h-4 mr-2" /> : <Icons.Hourglass className="w-4 h-4 mr-2" />}
              Loading: {diagnosticData.useStudentsHook.loading ? 'Yes' : 'No'}
            </div>
            {diagnosticData.useStudentsHook.error && (
              <div className="text-red-600">
                Error: {diagnosticData.useStudentsHook.error}
              </div>
            )}
          </div>
        </div>

        {/* Data Store */}
        <div className="border border-gray-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center"><Icons.Building className="w-5 h-5 mr-2" /> Data Store</h3>
          <div className="space-y-2 text-sm">
            <div className={`flex items-center ${diagnosticData.dataStore.studentsCount > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {diagnosticData.dataStore.studentsCount > 0 ? <Icons.CheckCircle className="w-4 h-4 mr-2" /> : <Icons.XCircle className="w-4 h-4 mr-2" />}
              Students: {diagnosticData.dataStore.studentsCount}
            </div>
            <div className={`flex items-center ${!diagnosticData.dataStore.loading ? 'text-green-600' : 'text-yellow-600'}`}>
              {!diagnosticData.dataStore.loading ? <Icons.CheckCircle className="w-4 h-4 mr-2" /> : <Icons.Hourglass className="w-4 h-4 mr-2" />}
              Loading: {diagnosticData.dataStore.loading ? 'Yes' : 'No'}
            </div>
            {diagnosticData.dataStore.error && (
              <div className="text-red-600">
                Error: {diagnosticData.dataStore.error}
              </div>
            )}
          </div>
        </div>

        {/* Direct Firestore Fetch */}
        <div className="border border-gray-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center"><Icons.Fire className="w-5 h-5 mr-2" /> Direct Firestore</h3>
          <div className="space-y-2 text-sm">
            <div className={`flex items-center ${diagnosticData.directFetch.studentsCount > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {diagnosticData.directFetch.studentsCount > 0 ? <Icons.CheckCircle className="w-4 h-4 mr-2" /> : <Icons.XCircle className="w-4 h-4 mr-2" />}
              Students: {diagnosticData.directFetch.studentsCount}
            </div>
            {diagnosticData.directFetch.error && (
              <div className="text-red-600">
                Error: {diagnosticData.directFetch.error}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sample Student Data */}
      {students && students.length > 0 && (
        <div className="mt-6 border border-gray-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center"><Icons.ClipboardDocumentList className="w-5 h-5 mr-2" /> Sample Student Data</h3>
          <pre className="bg-gray-50 p-3 rounded text-xs overflow-x-auto">
            {JSON.stringify(students[0], null, 2)}
          </pre>
        </div>
      )}

      {/* Console Log Section */}
      <div className="mt-6 border border-gray-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center"><Icons.Monitor className="w-5 h-5 mr-2" /> Console Logs</h3>
        <p className="text-sm text-gray-600 mb-2">Check the browser console for additional logging information.</p>
        <button 
          onClick={() => {
            console.log('🔍 Manual Diagnostic Data:', diagnosticData);
            console.log('🔍 Students from useStudents:', students);
            console.log('🔍 Store students:', storeStudents);
            console.log('🔍 Direct fetch result:', directFetchResult);
          }}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm"
        >
          Log Current State to Console
        </button>
      </div>

      {/* Recommended Actions */}
      <div className="mt-6 border border-yellow-200 bg-yellow-50 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-yellow-800 mb-3 flex items-center"><Icons.LightBulb className="w-5 h-5 mr-2" /> Recommended Actions</h3>
        <ul className="text-sm text-yellow-700 space-y-1">
          {!diagnosticData.auth.isAuthenticated && <li>• Please log in to load student data</li>}
          {diagnosticData.auth.isAuthenticated && diagnosticData.directFetch.studentsCount === 0 && (
            <li>• Check Firestore security rules for the 'students' collection</li>
          )}
          {diagnosticData.auth.isAuthenticated && diagnosticData.directFetch.studentsCount > 0 && diagnosticData.useStudentsHook.studentsCount === 0 && (
            <li>• Issue with real-time subscription - check DataProvider initialization</li>
          )}
          {diagnosticData.useStudentsHook.error && (
            <li>• Resolve useStudents hook error: {diagnosticData.useStudentsHook.error}</li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default StudentLoadingDiagnostic;

